
import {
  doc, setDoc, deleteDoc, onSnapshot, collection,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebaseService';
import { LiveLocation, LiveViewer } from '../types';

const POSITIONS_PATH = (groupId: string) => `live_locations/${groupId}/positions`;
const VIEWERS_PATH = (groupId: string) => `live_locations/${groupId}/viewers`;
const STALE_MS = 5 * 60 * 1000; // 5 minutes
const VIEWER_STALE_MS = 2 * 60 * 1000; // 2 minutes

// --- Publishing own position ---

let _watchId: number | null = null;
let _sessionId: string | null = null;
let _activeGroupId: string | null = null;

export function startSharingLocation(
  userId: string,
  userName: string,
  userPhoto: string | undefined,
  groupId: string,
  groupName: string,
  gatedWatch?: (success: PositionCallback, error?: PositionErrorCallback | null, options?: PositionOptions) => number | undefined,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (_watchId !== null) { resolve(); return; }

    _sessionId = `${userId}_${Date.now()}`;
    _activeGroupId = groupId;

    let resolved = false;

    const watchFn = gatedWatch || navigator.geolocation.watchPosition.bind(navigator.geolocation);
    _watchId = watchFn(
      async (pos) => {
        if (!resolved) { resolved = true; resolve(); }

        const loc: LiveLocation = {
          userId,
          userName,
          userPhoto: userPhoto || undefined,
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: Math.round(pos.coords.accuracy),
          heading: pos.coords.heading ?? undefined,
          speed: pos.coords.speed ?? undefined,
          updatedAt: Date.now(),
          sessionId: _sessionId!,
          groupId,
          groupName,
        };
        // Fire-and-forget Firestore write so UI is never blocked
        setDoc(doc(db, POSITIONS_PATH(groupId), userId), {
          ...loc,
          _serverTs: serverTimestamp(),
        }).catch(() => { /* ignore write errors */ });
      },
      (err) => {
        console.warn('Location sharing GPS error:', err);
        if (!resolved) { resolved = true; reject(err); }
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
  });
}

export async function stopSharingLocation(userId: string, groupId: string): Promise<void> {
  if (_watchId !== null) {
    navigator.geolocation.clearWatch(_watchId);
    _watchId = null;
  }
  _sessionId = null;
  _activeGroupId = null;
  try {
    await deleteDoc(doc(db, POSITIONS_PATH(groupId), userId));
  } catch { /* ignore */ }
}

// --- Subscribing to group positions ---

export function subscribeToGroupLocations(
  groupId: string,
  ownUserId: string,
  callback: (locations: LiveLocation[]) => void
): () => void {
  const col = collection(db, POSITIONS_PATH(groupId));
  const unsub = onSnapshot(col, (snap) => {
    const now = Date.now();
    const locs: LiveLocation[] = [];
    snap.forEach((d) => {
      const data = d.data() as LiveLocation;
      if (data.userId === ownUserId) return;
      if (now - data.updatedAt > STALE_MS) return;
      locs.push(data);
    });
    callback(locs);
  }, () => callback([]));
  return unsub;
}

// --- Viewer presence ---

export function setViewerPresence(
  groupId: string,
  userId: string,
  userName: string,
  userPhoto?: string,
): void {
  const viewer: LiveViewer = { userId, userName, userPhoto, lastSeen: Date.now() };
  setDoc(doc(db, VIEWERS_PATH(groupId), userId), viewer).catch(() => {});
}

export function clearViewerPresence(groupId: string, userId: string): void {
  deleteDoc(doc(db, VIEWERS_PATH(groupId), userId)).catch(() => {});
}

export function subscribeToGroupViewers(
  groupId: string,
  callback: (viewers: LiveViewer[]) => void,
): () => void {
  const col = collection(db, VIEWERS_PATH(groupId));
  const unsub = onSnapshot(col, (snap) => {
    const now = Date.now();
    const viewers: LiveViewer[] = [];
    snap.forEach((d) => {
      const data = d.data() as LiveViewer;
      if (now - data.lastSeen > VIEWER_STALE_MS) return;
      viewers.push(data);
    });
    callback(viewers);
  }, () => callback([]));
  return unsub;
}
